import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { EmojiApi } from "@app/shared/sdk";
import { environment } from "@env/environment";

export interface DressUpGroup {
  bodyPart: string;
  emojiIds: string[];
}

// FINAL X p28-36. Order matters for the admin UI (owner decision, 2026-08-03)
// but is not enforced server-side — `Game.validate('dressUpGroups', ...)` in
// common/models/game.js only checks there are 6 groups of 8 emojis (48
// total), not which body part is in which position.
export const DRESS_UP_BODY_PARTS = [
  "hats",
  "glasses",
  "shirts",
  "pants",
  "shoes",
  "necklaces",
];

// Must match Game.DRESS_UP_GROUP_SIZE / Game.DRESS_UP_GROUP_COUNT in
// common/models/game.js.
export const DRESS_UP_GROUP_SIZE = 8;

export function createEmptyDressUpGroups(): DressUpGroup[] {
  return DRESS_UP_BODY_PARTS.map((bodyPart) => ({ bodyPart, emojiIds: [] }));
}

// Fills in any missing body-part groups (e.g. an older document, or one
// edited outside the admin) without dropping emojiIds that are already
// present, so a partially-populated game doesn't lose data when reopened.
export function normalizeDressUpGroups(
  groups: DressUpGroup[] | undefined | null
): DressUpGroup[] {
  const byBodyPart: { [key: string]: string[] } = {};
  if (Array.isArray(groups)) {
    groups.forEach((g) => {
      if (g && typeof g.bodyPart === "string") {
        byBodyPart[g.bodyPart] = Array.isArray(g.emojiIds) ? [...g.emojiIds] : [];
      }
    });
  }
  return DRESS_UP_BODY_PARTS.map((bodyPart) => ({
    bodyPart,
    emojiIds: byBodyPart[bodyPart] || [],
  }));
}

@Component({
  selector: "app-dress-up-config",
  templateUrl: "./dress-up-config.component.html",
  styleUrls: ["./dress-up-config.component.scss"],
})
export class DressUpConfigComponent implements OnInit {
  @Input() groups: DressUpGroup[] = createEmptyDressUpGroups();
  @Output() groupsChange = new EventEmitter<DressUpGroup[]>();

  // Same imageSrc/url pairing convention as automatchButtonArt/selfPickButtonArt
  // in GameSettingsComponent (relative path + resolved S3 url after upload).
  @Input() figureImage: string | null = null;
  @Input() figureImageUrl: string | null = null;
  @Output() figureUpload = new EventEmitter<File>();
  @Output() figureRemoved = new EventEmitter<void>();

  readonly bodyParts = DRESS_UP_BODY_PARTS;
  readonly groupSize = DRESS_UP_GROUP_SIZE;

  s3BucketUrl = environment.s3BucketUrl;
  fallbackImage = "/assets/images/image-not-found-150x150.png";

  emojis: any[] = [];
  loadingEmojis = false;
  filterText = "";
  selectedTabIndex = 0;

  constructor(private emojiApi: EmojiApi) {}

  ngOnInit() {
    this.loadingEmojis = true;
    this.emojiApi
      .find({ where: { isActive: true }, order: "order ASC", limit: 1000 })
      .subscribe(
        (emojis: any) => {
          this.emojis = Array.isArray(emojis) ? emojis : Object.values(emojis || {});
          this.loadingEmojis = false;
        },
        () => {
          this.loadingEmojis = false;
        }
      );
  }

  currentSetIndex = 0;
  readonly setSize = 48;
  Math = Math;

  get allFilteredEmojis(): any[] {
    if (!this.filterText) return this.emojis;
    const needle = this.filterText.toLowerCase();
    return this.emojis.filter(
      (e) => e.image && e.image.toLowerCase().indexOf(needle) !== -1
    );
  }

  get totalFilteredEmojisCount(): number {
    return this.allFilteredEmojis.length;
  }

  get totalSets(): number {
    return Math.ceil(this.totalFilteredEmojisCount / this.setSize) || 1;
  }

  get emojiSets(): number[] {
    return Array.from({ length: this.totalSets }, (_, i) => i);
  }

  get filteredEmojis(): any[] {
    const start = this.currentSetIndex * this.setSize;
    return this.allFilteredEmojis.slice(start, start + this.setSize);
  }

  prevSet() {
    if (this.currentSetIndex > 0) {
      this.currentSetIndex--;
    }
  }

  nextSet() {
    if (this.currentSetIndex < this.totalSets - 1) {
      this.currentSetIndex++;
    }
  }

  get total(): number {
    return this.groups.reduce(
      (sum, g) => sum + (g && Array.isArray(g.emojiIds) ? g.emojiIds.length : 0),
      0
    );
  }

  get totalTarget(): number {
    return this.bodyParts.length * this.groupSize;
  }

  get isComplete(): boolean {
    return (
      this.total === this.totalTarget &&
      this.groups.every((g) => g.emojiIds && g.emojiIds.length === this.groupSize)
    );
  }

  private groupFor(bodyPart: string): DressUpGroup | undefined {
    return this.groups.find((g) => g.bodyPart === bodyPart);
  }

  countFor(bodyPart: string): number {
    const group = this.groupFor(bodyPart);
    return group && group.emojiIds ? group.emojiIds.length : 0;
  }

  isSelected(bodyPart: string, emojiId: string): boolean {
    const group = this.groupFor(bodyPart);
    return !!group && group.emojiIds.indexOf(emojiId) !== -1;
  }

  isFull(bodyPart: string): boolean {
    return this.countFor(bodyPart) >= this.groupSize;
  }

  toggleEmoji(bodyPart: string, emojiId: string) {
    if (!emojiId) return;
    const nextGroups = this.groups.map((g) => ({
      bodyPart: g.bodyPart,
      emojiIds: Array.isArray(g.emojiIds) ? [...g.emojiIds] : [],
    }));
    const group = nextGroups.find((g) => g.bodyPart === bodyPart);
    if (!group) return;

    const idx = group.emojiIds.indexOf(emojiId);
    if (idx !== -1) {
      group.emojiIds.splice(idx, 1);
    } else {
      if (group.emojiIds.length >= this.groupSize) return;
      group.emojiIds.push(emojiId);
    }

    this.groups = nextGroups;
    this.groupsChange.emit(this.groups);
  }

  onFigureFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.figureUpload.emit(input.files[0]);
      input.value = "";
    }
  }

  onFigureRemoved() {
    this.figureRemoved.emit();
  }

  resolvedFigureSrc(): string {
    if (this.figureImageUrl) return this.figureImageUrl;
    if (this.figureImage) return this.s3BucketUrl + this.figureImage;
    return this.fallbackImage;
  }

  onImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.getAttribute("src") === this.fallbackImage) return;
    img.src = this.fallbackImage;
  }
}
